import { Badge, Center, Grid, Paper, Stack, Text, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { ColDef, CompanyLogo, NewTable, VendorSalesPersonFilter } from '@ospace/shared'
import dayjs from 'dayjs'
import { useState } from 'react'

import { DealModal } from './DealModal'
import classes from './OpportunitiesTable.module.css'

type TableData = {
  dealCampaignId: number
  dealId: number
  dealData: any
  companyData: any
  contactData: any
  createdAt: string
  noteCreatedAt: string
  dealViewedAt: string
  noteBody: string
  newOpportunity: boolean
  newActivity: boolean
  conversationNotes?: string
  meetingNotes?: string
}

type TransformedDeal = {
  campaignId: number
  dealId: number
  status: string
  domain: string
  company: string
  contact: string
  role: string
  newActivity: boolean
  newOpportunity: boolean
  noteBody: string
  createdAt: string
  dealViewedAt: string | null
  conversationNotes: string
  meetingNotes: string
}

enum DealLabel {
  TotalOpportunities = 'Total Opportunities',
  Meetings = 'Meetings',
  MeetingsBooked = 'Meetings Booked',
  HighTouchNurture = 'High Touch Nurture',
  NewOpportunities = 'New Opportunities',
  NewActivities = 'New Activities',
  Opportunities = 'Opportunities',
}

enum DealStatus {
  leadReview = 'a Lead – Review',
  leadRequested = 'a Lead – Requested',
  leadApproved = 'a Lead Approved',
  highTouchNurture = 'b High Touch Nurture',
  highTouchNurtureRescheduleClient = 'b High Touch Nurture - Reschedule Client',
  highTouchNurtureRescheduleProspect = 'b High Touch Nurture - Reschedule Prospect',
}

const status = {
  'a Lead – Requested': 'HTN',
  'b High Touch Nurture': 'HTN',
  'b High Touch Nurture - Reschedule Client': 'HTN - RC',
  'b High Touch Nurture - Reschedule Prospect': 'HTN - RP',
  'a Lead – Review': 'Meeting Booked',
  'a Lead Approved': 'Meeting Complete',
}

const stripExternalResourceTags = (html: string) => {
  let result = html

  const pairedTags = ['video', 'audio', 'picture', 'iframe', 'object', 'script', 'style', 'svg']
  for (const tag of pairedTags) {
    result = result.replace(new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}>`, 'gi'), '')
  }

  result = result.replace(
    /<(img|embed|source|track|iframe|video|audio|object|link|base|input)\b[^>]*\/?>/gi,
    ''
  )

  // Inline styles with url() can trigger background-image fetches
  result = result.replace(/\sstyle=(["'])[^"']*url\([^"']*\)[^"']*\1/gi, '')

  return result
}

const extractTextFromHTML = (htmlOrText: string) => {
  const div = document.createElement('div')
  div.innerHTML = stripExternalResourceTags(htmlOrText)

  let text = div.textContent || div.innerText || ''

  // Normalize all whitespace (tabs, newlines, multiple spaces)
  text = text
    .replace(/[\t\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  // Remove `". "` or `", "` style separators between quoted strings
  text = text.replace(/"\s*[.]\s*"/g, '. ') // safest: collapse to period + space

  // Escape all quotes
  text = text.replace(/"/g, '""')

  // remove ", " forced
  text = text.replaceAll(', ', '，')

  // Wrap the whole string in quotes
  return `${text}`
}

const filterAndSortDeals: any = (data: TransformedDeal[], statuses: DealStatus[]) => {
  return data
    .filter((deal: TransformedDeal) => statuses.includes(deal.status as DealStatus))
    .sort((a: TransformedDeal, b: TransformedDeal) => {
      if (a.newOpportunity && !b.newOpportunity) return -1
      if (a.newActivity && !b.newActivity) return -1
      return 0
    })
}

const OpportunitiesTable = ({
  deals,
  vendorSalesPerson,
}: {
  deals: any
  vendorSalesPerson: any
}) => {
  const [opened, { open, close }] = useDisclosure(false)
  const [modalData, setModalData] = useState(deals)
  const colDef: ColDef[] = [
    {
      key: 'status',
      label: 'Status',
      element: (row) => (
        <Badge color='gray' variant='filled'>
          {status[row.status as keyof typeof status]}
        </Badge>
      ),
      hideHeader: true,
    },
    {
      key: 'company-logo',
      label: 'Company',
      element: (row) => <CompanyLogo domain={row.domain} companyName={row.company} />,
    },
    {
      key: 'contact',
      label: 'Contact',
      element: (row) => {
        return (
          <Stack gap={'xs'}>
            <Text>{row.contact}</Text>
            <Text className={classes.roleText}>{row.role}</Text>
          </Stack>
        )
      },
    },
    {
      key: 'meetingDate',
      label: 'Date',
      element: (row) => dayjs(row.meetingDate).format('DD/MM/YYYY'),
    },
    {
      key: 'note',
      label: 'Note',
      width: '400px',
      element: (row) => {
        const formattedNoteText = extractTextFromHTML(row.noteBody)
        const formattedConversationNoteText = extractTextFromHTML(row.conversationNotes)

        return (
          <Text truncate='end' lineClamp={4} style={{ whiteSpace: 'normal' }}>
            {formattedConversationNoteText} {formattedNoteText}
          </Text>
        )
      },
    },
    {
      key: 'newOpportunity',
      label: 'New Opportunity',
      element: (row) => (
        <>
          {row.newOpportunity ? (
            <Badge color='red' variant='filled'>
              New Opportunity
            </Badge>
          ) : row.newActivity ? (
            <Badge color='green' variant='filled'>
              New Activity
            </Badge>
          ) : null}
        </>
      ),
    },
  ]

  const data = deals?.data?.map((deal: TableData) => {
    return {
      email: deal.contactData?.properties?.email || '',
      phone: deal.contactData?.properties?.mobilePhone || deal.contactData?.properties?.phone || '',
      campaignId: deal.dealCampaignId,
      companyId: deal.companyData?.id,
      dealId: deal.dealId,
      status: deal.dealData?.properties.ospace_status,
      companyBackground: deal.dealData?.properties?.company_background || '',
      domain: deal.companyData?.properties?.domain || '',
      company: deal.companyData?.properties?.name || '-',
      companyData: deal.companyData?.properties || {},
      contact: (
        deal.dealData?.properties.contact_name ||
        `${deal.contactData?.properties?.firstname} ${deal.contactData?.properties?.lastname}` ||
        '-'
      )
        .replace(/undefined/g, '')
        .replace(/null/g, '')
        .replace(/NaN/g, '')
        .trim(),
      role: deal.contactData?.properties?.jobtitle || '-',
      newActivity: deal.noteCreatedAt > deal.dealViewedAt ? true : false, //Randonly set true or false
      newOpportunity: deal.dealViewedAt === null,
      noteBody: deal.noteBody || '-',
      createdAt: deal.dealData.createdAt,
      meetingNotes: deal.dealData?.properties?.meeting_notes || '',
      meetingDate: deal.dealData.properties.meeting_date || deal.dealData.updatedAt,
      dealViewedAt: deal?.dealViewedAt,
      conversationNotes: deal.dealData?.properties?.conversation_notes || '',
      revenueRange: deal.companyData?.properties?.ospace_revenue_range || '',
    }
  })

  const meetingCompleteData = filterAndSortDeals(data, [DealStatus.leadApproved])
  const meetingBookedData = filterAndSortDeals(data, [DealStatus.leadReview])
  const htnData = filterAndSortDeals(data, [
    DealStatus.leadRequested,
    DealStatus.highTouchNurture,
    DealStatus.highTouchNurtureRescheduleClient,
    DealStatus.highTouchNurtureRescheduleProspect,
  ])

  const finalData = [...meetingCompleteData, ...meetingBookedData, ...htnData].map((d) => {
    const companyId = d.companyId?.toString()

    const person = Object.values(vendorSalesPerson.data).find((v: any) =>
      companyId ? v.companyIds.includes(companyId) : false
    ) as { firstname: string; lastname: string } | undefined

    return {
      ...d,
      vendorSalesPerson: person ? `${person.firstname} ${person.lastname}` : '',
    }
  })
  const [filteredData, setFilteredData] = useState(finalData)
  const { SelectFilter, filteredData: vendorFilteredData } = VendorSalesPersonFilter({
    data: filteredData || [],
    vendorSalesPerson: vendorSalesPerson.data,
    filterKeyPath: 'companyId',
  })
  const Filter = (data: TableData[], setData: any, setCurrentPage: any) => {
    const [selected, setSelected] = useState(DealLabel.TotalOpportunities as string)

    const onClickHandler = (label: string) => {
      setSelected(label)
      setCurrentPage(1)

      switch (label) {
        case DealLabel.TotalOpportunities:
          return setFilteredData(finalData)
        case DealLabel.NewOpportunities:
          return setFilteredData(finalData.filter((deal: TableData) => deal.newOpportunity))
        case DealLabel.NewActivities:
          return setFilteredData(finalData.filter((deal: TableData) => deal.newActivity))
        case DealLabel.Meetings:
          return setFilteredData(
            finalData.filter((deal: any) => {
              return (
                deal.status === DealStatus.leadReview || deal.status === DealStatus.leadApproved
              )
            })
          )
        case DealLabel.HighTouchNurture:
          return setFilteredData(
            finalData.filter((deal: any) => {
              return (
                deal.status === DealStatus.leadRequested ||
                deal.status === DealStatus.highTouchNurture ||
                deal.status === DealStatus.highTouchNurtureRescheduleClient ||
                deal.status === DealStatus.highTouchNurtureRescheduleProspect
              )
            })
          )
        default:
          return setFilteredData(finalData)
      }
    }
    return (
      <Center m={'md'}>
        <Grid>
          {[
            {
              label: DealLabel.TotalOpportunities,
              value: finalData?.length,
            },
            {
              label: DealLabel.Meetings,
              value:
                finalData?.filter((deal: any) => deal.status === DealStatus.leadReview).length +
                finalData?.filter((deal: any) => deal.status === DealStatus.leadApproved).length,
            },
            {
              label: DealLabel.HighTouchNurture,
              value:
                finalData?.filter((deal: any) => deal.status === DealStatus.leadRequested).length +
                finalData?.filter((deal: any) => deal.status === DealStatus.highTouchNurture)
                  .length +
                finalData?.filter(
                  (deal: any) => deal.status === DealStatus.highTouchNurtureRescheduleClient
                ).length +
                finalData?.filter(
                  (deal: any) => deal.status === DealStatus.highTouchNurtureRescheduleProspect
                ).length,
            },
            {
              label: DealLabel.NewOpportunities,
              value: finalData?.filter((deal: any) => deal.newOpportunity).length,
            },
            {
              label: DealLabel.NewActivities,
              value: finalData?.filter((deal: any) => deal.newActivity).length,
            },
          ].map(({ label, value }) => (
            <Stack
              key={label}
              style={{
                cursor: 'pointer',
                borderRight: label !== DealLabel.NewActivities ? '1px solid gray' : '',
                backgroundColor: selected === label ? '#e8eef3' : '',
              }}
              gap={'xs'}
              px={'lg'}
              my={'lg'}
              onClick={() => onClickHandler(label)}
            >
              <Text c='dimmed' fw={500} fz='14px' tt={'uppercase'} mt={10}>
                {label}
              </Text>
              <Center fw={700} fz='24px' c='gray.7' mb={5}>
                {value}
              </Center>
            </Stack>
          ))}
        </Grid>
      </Center>
    )
  }

  const exportData = vendorFilteredData.map((item) => {
    const formattedNoteText = extractTextFromHTML(item.noteBody)
    const formattedConversationNoteText = extractTextFromHTML(item.conversationNotes)
    const formattedMeetingNoteText = extractTextFromHTML(item.meetingNotes)

    return {
      ...(vendorSalesPerson.enabled && {
        vendorSalesPerson: item.vendorSalesPerson,
      }),
      campaignId: item.campaignId,
      companyPostCode: item.companyData?.zip || '-',
      companyCity: item.companyData?.city || '-',
      companyState: item.companyData?.state || '-',
      companyCountry: item.companyData?.country || '-',
      companyId: item.companyId,
      dealId: item.dealId,
      status: status[item.status as keyof typeof status],
      domain: item.domain,
      company: item.company,
      contact: item.contact,
      email: item.email || '-',
      phone: item.phone || '-',
      role: item.role,
      revenueRange: item.revenueRange || '-',
      meetingDate: dayjs(item.meetingDate).format('DD/MM/YYYY'),
      conversationNotes: `${formattedConversationNoteText} ${formattedNoteText}`,
      meetingNotes: `${formattedMeetingNoteText}`,
    }
  })

  return data?.length > 0 ? (
    <Paper withBorder p='xl' radius='md' shadow='xs'>
      <NewTable
        enableExportButton
        tableData={vendorFilteredData}
        exportData={exportData}
        colDef={colDef}
        hideHeader={true}
        filter={Filter}
        title='Opportunities'
        tableProps={{
          itemsPerPage: 6,
          verticalSpacing: 'lg',
          horizontalSpacing: 0,
        }}
        modal={{
          component: <DealModal opened={opened} onClose={close} data={modalData} />,
          setModalData,
          open,
        }}
        vendorSalesPersonFilter={vendorSalesPerson.enabled ? SelectFilter : <></>}
      />
    </Paper>
  ) : (
    <>
      <Title>No Opportunities</Title>
    </>
  )
}

export default OpportunitiesTable
